import { Box, Typography, CircularProgress, styled } from "@mui/material";
import { useGetTSRDetailsQuery } from "../../../useQuery";

const SynopContainer = styled(Box)`
  padding: 30px 25px;
  margin-bottom: 40px;
  background-color: white;
  border-radius: 0px 0px 12px 12px;
  box-shadow: 0px 24px 23.3px 0px rgba(0, 0, 0, 0.03);
`;

const CardHeader = styled(Typography)`
  color: #000000;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 20px;
`;

const Item = styled(Box)`
  display: flex;
  flex-direction: column;
  row-gap: 3px;
`;

const ItemHeader = styled(Typography)`
  color: #8089a8;
  font-size: 13px;
  font-weight: 500;
`;

const ItemValue = styled(Typography)`
  color: #2b3539;
  font-size: 16px;
  font-weight: 400;
  overflow-wrap: anywhere;
`;

const TwoColGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

const DivergenceAnalysis = ({ currentEditItem }) => {
  const tsrid = currentEditItem?.idtest_step_result;
  const { data, isLoading, isError } = useGetTSRDetailsQuery(tsrid);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box p={3}>
        <ItemValue style={{ color: "red" }}>Failed to load divergence analysis data.</ItemValue>
      </Box>
    );
  }

  const aiError = data.error_from_AI
    ? `[${data.error_from_AI.result ?? "N/A"}] ${data.error_from_AI.reason ?? ""}`
    : "-";

  return (
    <Box>
      {/* Element & Locator checks */}
      <SynopContainer>
        <CardHeader>Element &amp; Locator Match</CardHeader>
        <TwoColGrid>
          <Item>
            <ItemHeader>Is Element Same</ItemHeader>
            <ItemValue>
              {data.isElementSame != null ? (data.isElementSame ? "Yes" : "No") : "-"}
            </ItemValue>
          </Item>
          <Item>
            <ItemHeader>Is Locator Same</ItemHeader>
            <ItemValue>
              {data.isLocatorSame != null ? (data.isLocatorSame ? "Yes" : "No") : "-"}
            </ItemValue>
          </Item>
        </TwoColGrid>
      </SynopContainer>

      {/* Locator details */}
      <SynopContainer>
        <CardHeader>Locator Details</CardHeader>
        <TwoColGrid>
          <Item>
            <ItemHeader>Current Locator</ItemHeader>
            <ItemValue>{data.foundBy?.currentLocator || "-"}</ItemValue>
          </Item>
          <Item>
            <ItemHeader>Last Passed Locator</ItemHeader>
            <ItemValue>{data.foundBy?.lastPassedLocator || "-"}</ItemValue>
          </Item>
        </TwoColGrid>
      </SynopContainer>

      {/* Outer HTML */}
      <SynopContainer>
        <CardHeader>Outer HTML</CardHeader>
        <TwoColGrid>
          <Item>
            <ItemHeader>Current Outer HTML</ItemHeader>
            <ItemValue>{data.outerhtml?.currentOuterhtml || "-"}</ItemValue>
          </Item>
          <Item>
            <ItemHeader>Last Passed Outer HTML</ItemHeader>
            <ItemValue>{data.outerhtml?.lastPassedOuterhtml || "-"}</ItemValue>
          </Item>
        </TwoColGrid>
      </SynopContainer>

      {/* Errors */}
      <SynopContainer>
        <CardHeader>Errors</CardHeader>
        <TwoColGrid>
          <Item>
            <ItemHeader>Error Message in Action</ItemHeader>
            <ItemValue>{data.errorMessageInAction || "-"}</ItemValue>
          </Item>
          <Item>
            <ItemHeader>AI Error</ItemHeader>
            <ItemValue>{aiError}</ItemValue>
          </Item>
        </TwoColGrid>
      </SynopContainer>
    </Box>
  );
};

export default DivergenceAnalysis;
